import { useMutation } from '@apollo/client';
import { ResolveConversationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { ResolveConversationTRPCInput } from './useResolveConversationTRPC';
import { useResolveConversationTRPC } from './useResolveConversationTRPC';

/**
 * Flag-gated wrapper (RSP-6496). Both paths return `{ Id }`.
 */
export const useResolveConversation = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [resolveConversationGraphQL, graphqlState] = useMutation(
    ResolveConversationDocument
  );

  const trpcMutation = useResolveConversationTRPC();

  const resolveConversation = async (
    input: ResolveConversationTRPCInput
  ): Promise<{ Id: string }> => {
    if (trpcEnabled) {
      return trpcMutation.resolveConversation(input);
    }

    const result = await resolveConversationGraphQL({
      variables: { Id: input.Id },
    });
    if (!result.data?.update_conversation_by_pk) {
      throw new Error('Failed to resolve conversation');
    }

    return { Id: result.data.update_conversation_by_pk.Id };
  };

  if (trpcEnabled) {
    return {
      resolveConversation,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    resolveConversation,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
